import { Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';

import Details from './Details';
import {
	addNewRecruit,
	addUpdatedRecruit,
} from '../../../services/recruitsService';

import { setAddOrUpdateRecruit } from '../../../store/reducers/addOrUpdateRecruitReducer';
import {
	initialState,
	setRecruitForm,
} from '../../../store/reducers/recruitFormReducer';
import { RootState } from '../../../store/store';
import {
	useGetPaymentTypesQuery,
	useGetRecruitDetailsByIdQuery,
	useGetRolesQuery,
} from '../../../store/apiSlices/recruitApiSlice';
import { setGlobalSave } from '../../../store/reducers/globalSaveReducer';
import { setDirty } from '../../../store/reducers/unSavedChangesReducer';

import { DropdownModel } from '../../../models/dropdown.model';
import {
	OutsideRecruiterDetails,
	ProductionDetails,
	RecruitInfo,
	StaffDetails,
} from '../../../models/recruitInfo.model';
import leftArrow from '../../../assets/icons/leftArrow.svg';
import refreshIcon from '../../../assets/icons/refresh.svg';

import { DateConstants, route } from '../../../constants';
import { OfficerRoles, PaymentMethods } from '../../../enums/enum';
import { toastHandler } from '../../../utils/toastHandler';
import { StatusCode } from '../../../enums/httpStatus.enum';
import {
	formatPhoneNumber,
	phoneNumberWithCountryCode,
} from '../../../utils/phone-format';
import { useFormikRefs } from '../../../hooks/useFormikRef';
import useComponentReset from '../../../hooks/useComponentReset';
import { FormikRefs, emailRegExp } from '../../../constants';
import {
	getCurrencyFormat,
	removeCurrencyFormat,
} from '../../../functions/stringFunctions';
import { useAddOrUpdateNotesMutation } from '../../../store/apiSlices/notesApiSlice';
import { useAddOrUpdateAttachmentsMutation } from '../../../store/apiSlices/attachmentsApiSlice';
import { setNotesProperties } from '../../../store/reducers/notesReducer';
import { setAttachmentProperties } from '../../../store/reducers/attachmentsReducer';
import { MAX_FILES } from '../../../constants';
import VerticalDivider from '../../../components/VerticalDivider';
import SidePopup from '../../../components/SidePopup';
import AuditLog from '../../../components/AuditLog';
import { getFormattedDateString } from './../../../functions/dateFunction';

const RecruitDetails = ({
	id,
	isAddingDetails,
}: {
	id: string;
	isAddingDetails: boolean;
}) => {
	const [searchParams] = useSearchParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const recruit_id = searchParams.get('id');
	const formKey = FormikRefs.mainFormikRef;
	const { registerRef, unregisterRef } = useFormikRefs();
	const user = useSelector((store: RootState) => store.user);
	const globalSave = useSelector((state: RootState) => state.globalSave);
	const notes = useSelector((state: RootState) => state.notesReducer);
	const userInfo = useSelector((state: RootState) => state.user);
	const addOrUpdateRecruit = useSelector(
		(state: RootState) => state.addOrUpdateRecruit,
	);
	const attachmentsReducer = useSelector(
		(state: RootState) => state.attachmentsReducer,
	);
	const recruitForm = useSelector((state: RootState) => state.recruitForm);
	const [disableSave, setDisableSave] = useState(false);
	const [recruitInfo, setRecruitInfo] = useState(initialState);
	const [auditLog, setAuditLog] = useState(false);
	const recruitDetails = useGetRecruitDetailsByIdQuery(
		{
			userId: id,
			userEmail: user.user_email,
		},
		{ refetchOnMountOrArgChange: true },
	);
	const rolesMasterData = useGetRolesQuery(null);
	const [roles, setRoles] = useState<DropdownModel[]>([]);
	const { formikRefs } = useFormikRefs();
	let isSectionDirty = {
		editRecruit: false,
		notes: false,
		attachment: false,
	};

	const [uploadFile, uploadFileStatus] = useAddOrUpdateAttachmentsMutation();
	const [updateNotes, updateNotesProps] = useAddOrUpdateNotesMutation();
	const paymentMethodData = useGetPaymentTypesQuery(null);
	const formikRef = useRef<FormikProps<RecruitInfo>>(null);

	useComponentReset({ reset: () => handleUnmount() });

	useEffect(() => {
		registerRef(formKey, { current: formikRef.current });

		return () => {
			unregisterRef(formKey);
		};
	}, [formKey, formikRef, registerRef, unregisterRef]);

	const handleUnmount = () => {
		dispatch(setRecruitForm({ ...initialState }));
	};

	const getFormattedRecruitInfo = (data: RecruitInfo) => {
		const outside_recruiter_details = {
			is_outside_recruiter:
				data.outside_recruiter_details.is_outside_recruiter || false,
			name: data.outside_recruiter_details.name || '',
			recruiter_bpa: data.outside_recruiter_details.recruiter_bpa || null,
			payment_type_id:
				data.outside_recruiter_details.payment_type_id || null,
			bps:
				Number(data.outside_recruiter_details.bps || null).toString() ||
				null,
			bps_months:
				Number(
					data.outside_recruiter_details.bps_months || null,
				).toString() || null,
			flat_fee:
				getCurrencyFormat(
					data.outside_recruiter_details.flat_fee || '',
				) || '',
			other_method: data.outside_recruiter_details.other_method || '',
		};
		const production_details = {
			...data.production_details,
			monthly_volume:
				getCurrencyFormat(
					data.production_details?.monthly_volume || '0',
				) || null,
			monthly_units:
				Number(
					data.production_details?.monthly_units || null,
				).toString() || '0',
			conventional_loans:
				Number(
					data.production_details?.conventional_loans || null,
				).toString() || '0',
			bonds:
				Number(data.production_details?.bonds || null).toString() ||
				'0',
			government_loans:
				Number(
					data.production_details?.government_loans || null,
				).toString() || '0',
			broker:
				Number(data.production_details?.broker || null).toString() ||
				'0',
			jumbo:
				Number(data.production_details?.jumbo || null).toString() ||
				'0',
		};
		const general_details = {
			current_company: data.general_details.current_company || '',
			first_contacted_date:
				format(
					new Date(data.general_details.first_contacted_date || ''),
					'MM/dd/yyyy',
				) || null,
			last_contacted_date:
				format(
					new Date(data.general_details.last_contacted_date || ''),
					'MM/dd/yyyy',
				) || null,
			city: data.general_details.city || '',
			state_id: data.general_details.state_id || null,
			name: data.general_details.name || '',
			phone:
				phoneNumberWithCountryCode(
					data.general_details.phone as string,
				) || '',
			recruiting_manager_id:
				data.general_details.recruiting_manager_id || null,
			role_id: data.general_details.role_id || null,
			stage_id: data.general_details.stage_id || null,
			email: data.general_details.email || '',
			is_producing: data.general_details.is_producing || false,
			nmls_id: data.general_details.nmls_id || '',
		};
		const staff_details = {
			staff_total: Number(
				data.staff_details?.staff_total || null,
			).toString(),
			staff_lo: Number(data.staff_details?.staff_lo || null).toString(),
			staff_closers: Number(
				data.staff_details?.staff_closers || null,
			).toString(),
			staff_loa: Number(data.staff_details?.staff_loa || null).toString(),
			staff_underwriters: Number(
				data.staff_details?.staff_underwriters || null,
			).toString(),
			staff_office_id:
				data.staff_details?.staff_office_id ===
					'00000000-0000-0000-0000-000000000000' ||
				data.staff_details?.staff_office_id === null ||
				data.staff_details?.staff_office_id === undefined
					? null
					: data.staff_details?.staff_office_id,
			staff_processors: Number(
				data.staff_details?.staff_processors || null,
			).toString(),
			staff_others: Number(
				data.staff_details?.staff_others || null,
			).toString(),
		};
		const obj = {
			general_details,
			outside_recruiter_details,
			production_details,
			staff_details,
		};
		return obj;
	};

	const removeFormatChanges = (val: RecruitInfo) => {
		const {
			general_details,
			production_details,
			staff_details,
			outside_recruiter_details,
		} = val;
		const updatedGeneralDetails = {
			...general_details,
			role_id: general_details.role_id || '',
			stage_id:
				general_details?.stage_id ===
					'00000000-0000-0000-0000-000000000000' ||
				general_details?.stage_id === null
					? null
					: general_details?.stage_id,
			state_id:
				general_details?.state_id ===
					'00000000-0000-0000-0000-000000000000' ||
				general_details?.state_id === null
					? null
					: general_details?.state_id,
			phone: formatPhoneNumber(general_details?.phone || ''),
			recruiting_manager_id: general_details?.recruiting_manager_id || '',
			last_contacted_date: undefined,
			nmls_id: general_details.nmls_id ? general_details.nmls_id : null,
		};

		const updatedOutsideRecruiterDetails =
			outside_recruiter_details?.is_outside_recruiter
				? {
						is_outside_recruiter:
							outside_recruiter_details?.is_outside_recruiter,
						name: outside_recruiter_details?.name,
						payment_type_id:
							outside_recruiter_details.payment_type_id,
						...getOutsideRecruiterDetails(
							outside_recruiter_details,
						),
					}
				: { is_outside_recruiter: false };

		const updatedStaffDetails =
			roles.find((e) => e.value === OfficerRoles.branch)?.id ==
			general_details.role_id
				? {
						staff_total: Number(staff_details?.staff_total) || 0,
						staff_lo: Number(staff_details?.staff_lo) || 0,
						staff_loa: Number(staff_details?.staff_loa) || 0,
						staff_closers:
							Number(staff_details?.staff_closers) || 0,
						staff_processors:
							Number(staff_details?.staff_processors) || 0,
						staff_underwriters:
							Number(staff_details?.staff_underwriters) || 0,
						staff_office_id:
							staff_details?.staff_office_id ===
								'00000000-0000-0000-0000-000000000000' ||
							staff_details?.staff_office_id === null
								? null
								: (staff_details?.staff_office_id as string),
						staff_others: Number(staff_details?.staff_others) || 0,
					}
				: {};
		const updatedProductionDetails = {
			monthly_units: Number(production_details?.monthly_units) || 0,
			monthly_volume:
				Number(
					removeCurrencyFormat(
						(production_details?.monthly_volume as string) || '',
					),
				) || 0,
			conventional_loans:
				Number(production_details?.conventional_loans) || 0,
			government_loans: Number(production_details?.government_loans) || 0,
			bonds: Number(production_details?.bonds) || 0,
			broker: Number(production_details?.broker) || 0,
			jumbo: Number(production_details?.jumbo) || 0,
		};
		const obj = {
			general_details: updatedGeneralDetails,
			outside_recruiter_details: updatedOutsideRecruiterDetails,
			staff_details: updatedStaffDetails as StaffDetails,
			production_details: updatedProductionDetails as ProductionDetails,
		};
		return obj;
	};

	useEffect(() => {
		if (!recruitDetails?.currentData?.value) return;
		const data = recruitDetails?.currentData?.value;
		if (data && !isAddingDetails) {
			const isEditable =
				userInfo.roles.adminAppEditOthersOwnedRecruit ||
				(data.is_author && userInfo.roles.adminAppChangeRecruitStatus);
			setDisableSave(!isEditable);
			dispatch(
				setAddOrUpdateRecruit({
					isUpdatingRecruit: true,
					isAddingRecruit: false,
					isRecruitFieldsDisabled: !isEditable,
				}),
			);
			setRecruitInfo(data);
			const formattedRecruitInfo = getFormattedRecruitInfo(data);
			dispatch(
				setRecruitForm({
					...data,
					...formattedRecruitInfo,
				}),
			);
		}
	}, [recruitDetails?.currentData?.value, isAddingDetails]);

	const validationSchema = Yup.object().shape({
		general_details: Yup.object().shape({
			name: Yup.string()
				.matches(
					/^[A-Za-z]+(\s[a-zA-Z]+)+$/,
					'Please enter first name and last name ex:"Sam Curran"',
				)
				.min(2, 'Too Short!')
				.max(50, 'Too Long!')
				.required('Required'),
			phone: Yup.string().required('Required').min(17, 'Too Short'),
			email: Yup.string()
				.matches(emailRegExp, 'Email is not valid')
				.nullable(),
			role_id: Yup.string().required('Required'),
			stage_id: Yup.string().nullable(),
			city: Yup.string()
				.matches(/^[a-zA-Z\s]+$/, 'Please enter valid city')
				.nullable(),
			state_id: Yup.string().nullable(),
			current_company: Yup.string().nullable(),
			recruiting_manager_id: Yup.string().required('Required'),
			last_contacted_date: Yup.date().nullable(),
			first_contacted_date: Yup.date().nullable(),
			nmls_id: Yup.string()
				.nullable()
				.matches(/^[0-9]+$/, 'Please enter positive integer'),
			is_producing: Yup.boolean().nullable(),
		}),
		outside_recruiter_details: Yup.object().shape({
			is_outside_recruiter: Yup.boolean().nullable(),
			name: Yup.string().when('is_outside_recruiter', (val) => {
				if (val?.[0])
					return Yup.string()
						.required('Required')
						.matches(/^[a-zA-Z\s]+$/, 'Please enter valid name');
				return Yup.string().nullable();
			}),
			payment_type_id: Yup.string().when(
				'is_outside_recruiter',
				(val) => {
					if (val?.[0]) return Yup.string().required('Required');
					return Yup.string().nullable();
				},
			),
			bps: Yup.number().when(
				['is_outside_recruiter', 'payment_type_id'],
				(val) => {
					const isBPS =
						paymentMethodData.currentData?.value.find(
							(e) => e.id === val?.[1],
						)?.value === PaymentMethods.bps;
					if (val?.[0] && isBPS) {
						return Yup.number().required('Required');
					}
					return Yup.number().nullable();
				},
			),
			bps_months: Yup.number().when(
				['is_outside_recruiter', 'payment_type_id'],
				(val) => {
					const isBPS =
						paymentMethodData.currentData?.value.find(
							(e) => e.id === val?.[1],
						)?.value === PaymentMethods.bps;
					if (val?.[0] && isBPS)
						return Yup.number().required('Required');
					return Yup.number().nullable();
				},
			),
			flat_fee: Yup.string().when(
				['is_outside_recruiter', 'payment_type_id'],
				(val) => {
					const isFlatFee =
						paymentMethodData.currentData?.value.find(
							(e) => e.id === val?.[1],
						)?.value === PaymentMethods.flatFee;
					if (val?.[0] && isFlatFee)
						return Yup.string().required('Required');
					return Yup.string().nullable();
				},
			),
			other_method: Yup.string().when(
				['is_outside_recruiter', 'payment_type_id'],
				(val) => {
					const isOtherMethod =
						paymentMethodData.currentData?.value.find(
							(e) => e.id === val?.[1],
						)?.value === PaymentMethods.other;
					if (val?.[0] && isOtherMethod)
						return Yup.string().required('Required');
					return Yup.string().nullable();
				},
			),
		}),
		production_details: Yup.object().shape({
			monthly_units: Yup.number()
				.required('Required')
				.min(1, 'Enter a value greater than 0'),
			monthly_volume: Yup.string()
				.required('Required')
				.test(
					'is-zero',
					() => 'Enter a value greater than 0',
					(val) => !(Number(val.replace(/[^0-9]+/g, '')) <= 0),
				),
		}),
		// staff_details: Yup.object()
		// 	.nullable()
		// 	.when('general_details.role_id', (value) => {
		// 		return rolesMasterData.currentData?.value?.find(
		// 			(e) => e.value == 'Branch',
		// 		)?.id == (value?.[0] as string) && !isAddingDetails
		// 			? Yup.object()
		// 					.shape({
		// 						staff_total: Yup.number().required('Required'),
		// 						staff_lo: Yup.number().required('Required'),
		// 						staff_closers:
		// 							Yup.number().required('Required'),
		// 						staff_loa: Yup.number().required('Required'),
		// 						staff_underwriters:
		// 							Yup.number().required('Required'),
		// 						staff_office_id:
		// 							Yup.string().required('Required'),
		// 						staff_processors:
		// 							Yup.number().required('Required'),
		// 						staff_others: Yup.number().required('Required'),
		// 					})
		// 					.nullable()
		// 			: Yup.object().nullable();
		// 	}),
	});

	const handleBack = () => {
		window.history.back();
	};

	const handleAuditLogClose = () => {
		setAuditLog(false);
	};

	const getOutsideRecruiterDetails = (
		outside_recruiter_details: OutsideRecruiterDetails,
	) => {
		const val =
			paymentMethodData.currentData?.value.find(
				(e) => e.id === outside_recruiter_details?.payment_type_id,
			)?.value || '';
		switch (val) {
			case PaymentMethods.bps:
				return {
					bps: Number(outside_recruiter_details.bps) || 0,
					bps_months:
						Number(outside_recruiter_details.bps_months) || 0,
				};
			case PaymentMethods.flatFee:
				return {
					flat_fee:
						Number(
							removeCurrencyFormat(
								(outside_recruiter_details.flat_fee as string) ||
									'',
							),
						) || 0,
				};
			case PaymentMethods.other:
				return {
					other_method: outside_recruiter_details.other_method || '',
				};
		}
	};

	const onSaveDetails = async (
		values: RecruitInfo,
		{
			setFieldError,
		}: {
			setFieldError: (field: string, message: string | undefined) => void;
		},
	) => {
		dispatch(
			setDirty({
				isDirty: false,
				isNavigationAllowed: true,
				isModalOpen: false,
			}),
		);
		setDisableSave(true);
		const {
			general_details,
			outside_recruiter_details,
			production_details,
			staff_details,
			...rest
		} = values;
		const updated_user_email = user.user_email;
		const updatedGeneralDetails = {
			...general_details,
			role_id: general_details.role_id || '',
			stage_id:
				general_details?.stage_id ===
					'00000000-0000-0000-0000-000000000000' ||
				general_details?.stage_id === null
					? null
					: general_details?.stage_id,
			phone: formatPhoneNumber(general_details?.phone || ''),
			first_contacted_date:
				getFormattedDateString(
					general_details?.first_contacted_date as Date,
					DateConstants.isoDateTime,
				) || null,
			recruiting_manager_id: general_details.recruiting_manager_id || '',
			last_contacted_date: undefined,
			state_id:
				general_details?.state_id ===
					'00000000-0000-0000-0000-000000000000' ||
				general_details?.state_id === null
					? null
					: general_details?.state_id,
			nmls_id: general_details.nmls_id ? general_details.nmls_id : null,
		};

		const updatedOutsideRecruiterDetails =
			outside_recruiter_details?.is_outside_recruiter
				? {
						name: outside_recruiter_details.name,
						is_outside_recruiter:
							outside_recruiter_details.is_outside_recruiter,
						payment_type_id:
							outside_recruiter_details.payment_type_id ===
								'00000000-0000-0000-0000-000000000000' ||
							outside_recruiter_details.payment_type_id === null
								? null
								: outside_recruiter_details.payment_type_id,
						...getOutsideRecruiterDetails(
							outside_recruiter_details,
						),
					}
				: { is_outside_recruiter: false };
		const updatedProductionDetails = {
			monthly_units: Number(production_details?.monthly_units) || 0,
			monthly_volume:
				Number(
					removeCurrencyFormat(
						(production_details?.monthly_volume as string) || '',
					),
				) || 0,
		};

		try {
			toastHandler({
				isLoading: true,
				msg: 'Adding New Recruit',
			});
			const res = await addNewRecruit({
				user_name: userInfo.user_name,
				details: {
					general_details: { ...updatedGeneralDetails },
					outside_recruiter_details: updatedOutsideRecruiterDetails,
					initial_production_details: updatedProductionDetails,
					...rest,
					user_email: updated_user_email,
				},
			});
			if (res?.statusCode == StatusCode.SERVER_ERROR) {
				const resString = res?.value as unknown as string;
				toastHandler({
					isError: true,
					msg: resString,
				});
				dispatch(
					setDirty({
						isDirty: true,
						isNavigationAllowed: true,
						isModalOpen: false,
					}),
				);
				setDisableSave(false);
			} else if (res?.statusCode == StatusCode.OK) {
				toastHandler({
					isError: false,
					msg: 'Successfully Created Recruit',
				});
				if (res?.value?.recruit_id) {
					dispatch(
						setAddOrUpdateRecruit({
							isUpdatingRecruit: true,
							isAddingRecruit: false,
							isRecruitFieldsDisabled: false,
						}),
					);
					navigate(
						`/${route.recruitInformation}/${route.details}?id=${res?.value.recruit_id}`,
						{
							replace: true,
						},
					);
				}
			}
		} catch (e) {
			setDisableSave(false);
			toastHandler({
				isError: true,
				msg: 'Failed to update the changes',
			});
			dispatch(
				setDirty({
					isDirty: false,
					isNavigationAllowed: true,
					isModalOpen: false,
				}),
			);
		}
	};

	useEffect(() => {
		if (!rolesMasterData.currentData?.value?.length) return;
		const roles = rolesMasterData.currentData.value;
		setRoles(roles);
	}, [rolesMasterData]);

	const onUpdateDetails = async (
		values: RecruitInfo,
		{
			setFieldError,
		}: {
			setFieldError: (field: string, message: string | undefined) => void;
		},
	) => {
		const {
			general_details,
			outside_recruiter_details,
			staff_details,
			production_details,
			...restInfo
		} = values;

		const recruitInformation = removeFormatChanges(values);
		const { is_author, ...rest } = recruitInfo;
		try {
			toastHandler({
				isLoading: true,
				msg: 'Updating the changes',
			});
			const res = await addUpdatedRecruit({
				user_name: userInfo.user_name,
				new_details: {
					...recruitInformation,
					recruit_id: id,
					user_email: user.user_email,
				},
				old_details: {
					...rest,
					general_details: {
						...rest.general_details,
						role_id: rest.general_details.role_id || '',
						stage_id:
							rest.general_details?.stage_id ===
								'00000000-0000-0000-0000-000000000000' ||
							rest.general_details?.stage_id === null
								? null
								: rest.general_details?.stage_id,
						state_id:
							rest.general_details?.state_id ===
								'00000000-0000-0000-0000-000000000000' ||
							rest.general_details?.state_id === null
								? null
								: rest.general_details?.state_id,
						recruiting_manager_id:
							rest.general_details?.recruiting_manager_id || '',
						nmls_id: rest.general_details.nmls_id
							? rest.general_details.nmls_id
							: null,
						last_contacted_date: undefined,
					},
					recruit_id: id,
					user_email: user.user_email,
					staff_details:
						roles.find((e) => e.value === OfficerRoles.branch)
							?.id == rest.general_details.role_id
							? {
									staff_total:
										rest.staff_details?.staff_total,

									staff_lo: rest.staff_details?.staff_lo,
									staff_loa: rest.staff_details?.staff_loa,
									staff_closers:
										rest.staff_details?.staff_closers,

									staff_processors:
										rest.staff_details?.staff_processors,

									staff_underwriters:
										rest.staff_details?.staff_underwriters,

									staff_office_id:
										rest.staff_details?.staff_office_id ===
											'00000000-0000-0000-0000-000000000000' ||
										rest.staff_details?.staff_office_id ===
											null
											? null
											: (rest.staff_details
													?.staff_office_id as string),
									staff_others:
										rest.staff_details?.staff_others,
								}
							: {},
				},
			});
			if (res?.statusCode == StatusCode.SERVER_ERROR) {
				const resString = res?.value as unknown as string;
				toastHandler({
					isError: true,
					msg: resString,
				});
				isSectionDirty = {
					...isSectionDirty,
					editRecruit: false,
				};
				return;
			}
		} catch (e) {
			isSectionDirty = {
				...isSectionDirty,
				editRecruit: false,
			};
			dispatch(
				setDirty({
					isDirty: false,
					isNavigationAllowed: true,
					isModalOpen: false,
				}),
			);
			throw e;
		}
	};

	useEffect(() => {
		if (!globalSave.isGlobalSaveActivated) {
			const check = Object.values(globalSave.apisCalled).some(
				(e) => e === true,
			);
			setDisableSave(check);
		}
	}, [globalSave]);

	const onSubmit = async (
		values: RecruitInfo,
		{
			setFieldError,
		}: {
			setFieldError: (field: string, message: string | undefined) => void;
		},
	) => {
		if (isAddingDetails) {
			onSaveDetails(values, {
				setFieldError: setFieldError,
			});
		} else {
			dispatch(
				setGlobalSave({
					...globalSave,
					isGlobalSaveActivated: true,
				}),
			);
			dispatch(
				setAttachmentProperties({
					isUploadable: false,
				}),
			);
			dispatch(
				setNotesProperties({
					...notes,
					editableIndex: -1,
					addNotes: false,
				}),
			);
			isSectionDirty = {
				...isSectionDirty,
				editRecruit: true,
			};
			setDisableSave(true);
			try {
				await onUpdateDetails(values, {
					setFieldError: setFieldError,
				});
				if (
					formikRefs[FormikRefs.attachmentsFormikRef].current &&
					attachmentsReducer.isUploadable
				) {
					if (
						formikRefs[FormikRefs.attachmentsFormikRef].current
							?.values?.files?.length <= 0
					) {
						toastHandler({
							isError: true,
							msg: 'Please upload at least one file',
						});
						setDisableSave(false);
						dispatch(
							setGlobalSave({
								...globalSave,
								isGlobalSaveActivated: false,
							}),
						);
						return;
					} else if (
						formikRefs[FormikRefs.attachmentsFormikRef].current
							?.values?.files?.length > MAX_FILES
					) {
						toastHandler({
							isError: true,
							msg: 'Only 15 files are allowed at once',
						});
						setDisableSave(false);
						dispatch(
							setGlobalSave({
								...globalSave,
								isGlobalSaveActivated: false,
							}),
						);
						return;
					} else {
						try {
							const files =
								formikRefs[FormikRefs.attachmentsFormikRef]
									.current?.values?.files;
							await Promise.all(
								files?.map(async (file: File) => {
									return uploadFile({
										recruitid: recruit_id!,
										userEmail: userInfo.user_email,
										file: file,
									});
								}),
							);
							formikRefs[
								FormikRefs.attachmentsFormikRef
							].current?.resetForm();

							// eslint-disable-next-line @typescript-eslint/no-explicit-any
						} catch (e: any) {
							toastHandler({
								isError: true,
								msg: `Failed to upload attachments`,
							});
						}
						isSectionDirty = {
							...isSectionDirty,
							attachment: true,
						};
					}
				}
				if (
					formikRefs[FormikRefs.notesFormikRef].current?.values?.value
						?.length
				) {
					try {
						await updateNotes({
							...formikRefs[FormikRefs.notesFormikRef].current
								?.values,
							value: formikRefs[
								FormikRefs.notesFormikRef
							].current?.values?.value?.trim(),
						});
						formikRefs[
							FormikRefs.notesFormikRef
						].current?.setFieldValue('value', '');
						formikRefs[
							FormikRefs.notesFormikRef
						].current?.resetForm();
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
					} catch (e: any) {
						toastHandler({
							isError: true,
							msg:
								'Failed ' +
								(notes.editableIndex == -1
									? 'add notes'
									: 'update notes'),
						});
					}
					isSectionDirty = { ...isSectionDirty, notes: true };
				}
				const check = Object.values(isSectionDirty).some(
					(e) => e === true,
				);
				if (check) {
					toastHandler({
						isError: false,
						msg: 'Successfully updated the changes',
					});
				}
				recruitDetails.refetch();
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				toastHandler({
					isError: true,
					msg: 'Failed to update the changes',
				});
			}
			isSectionDirty = {
				attachment: false,
				editRecruit: false,
				notes: false,
			};
			dispatch(
				setGlobalSave({
					...globalSave,
					isGlobalSaveActivated: false,
				}),
			);
			setDisableSave(false);
		}
	};

	return (
		<div className="flex">
			<Formik
				innerRef={formikRef}
				initialValues={
					isAddingDetails
						? {
								...recruitForm,
								general_details: {
									...recruitForm.general_details,
								},
								outside_recruiter_details: {
									...recruitForm.outside_recruiter_details,
								},
								production_details: {
									...recruitForm.production_details,
								},
							}
						: { ...recruitForm }
				}
				enableReinitialize
				validationSchema={validationSchema}
				onSubmit={(values, { setFieldError }) => {
					onSubmit(values, { setFieldError });
				}}
			>
				{({ handleSubmit }) => (
					<div className="flex flex-col w-full gap-y-5">
						<div className="flex justify-between bg-white py-2 max-h-[calc(100vh-200px)] overflow-auto ">
							<div className="flex mx-4 items-center gap-x-4">
								<div
									className="flex gap-x-2 items-center cursor-pointer"
									onClick={() => handleBack()}
								>
									<img src={leftArrow} alt="" />
									<p className="font-medium text-sm">Back</p>
								</div>
								<div
									className={`py-1 flex flex-col font-semibold text-rcrm-primary-blue`}
								>
									{addOrUpdateRecruit?.isUpdatingRecruit
										? `Details`
										: 'Add New Recruit'}
									<div className="flex">
										<div className="w-3/4 h-1 bg-rcrm-primary-blue"></div>
										<div className="w-1/4 h-1 bg-rcrm-lime"></div>
									</div>
								</div>
							</div>
							{addOrUpdateRecruit?.isAddingRecruit ? (
								<div>
									<button
										disabled={disableSave}
										onClick={() => handleBack()}
										className={`bg-white px-4 py-2 rounded-lg border-2 border-transparent
										 ${disableSave ? 'bg-rcrm-primary-red' : 'hover:border-rcrm-hover-red'} text-rcrm-error mx-4 my-1`}
									>
										Cancel
									</button>
									<button
										type="submit"
										disabled={disableSave}
										onClick={() => handleSubmit()}
										className={`px-4 py-2 rounded-lg  
									${disableSave ? 'bg-rcrm-disabled-button  text-white' : 'bg-rcrm-primary-blue  text-white  hover:bg-rcrm-hover-blue '}
									`}
									>
										Save
									</button>
								</div>
							) : null}
							{addOrUpdateRecruit?.isUpdatingRecruit ? (
								<div className="flex items-center px-4 py-1 gap-x-4">
									<div
										className="flex gap-x-1 items-center cursor-pointer"
										onClick={() => setAuditLog(true)}
									>
										<img src={refreshIcon} alt="" />
										<p className="text-rcrm-primary-blue font-semibold">
											Audit Log
										</p>
									</div>
									{!addOrUpdateRecruit.isRecruitFieldsDisabled && (
										<>
											<VerticalDivider />
											<button
												type="submit"
												disabled={disableSave}
												onClick={() => handleSubmit()}
												className={`px-4 py-2 rounded-lg  
									${disableSave ? 'bg-rcrm-disabled-button  text-white' : 'bg-rcrm-primary-blue  text-white  hover:bg-rcrm-hover-blue '}
									`}
											>
												Save
											</button>
										</>
									)}
								</div>
							) : null}
						</div>
						<Details isAddingDetails={isAddingDetails} />
					</div>
				)}
			</Formik>
			{auditLog ? (
				<SidePopup
					isRight={true}
					isOpen={auditLog}
					onClose={handleAuditLogClose}
					sidePanelClassName="lg:w-[60vw]"
				>
					<AuditLog onCancel={handleAuditLogClose} />
				</SidePopup>
			) : null}
		</div>
	);
};
export default RecruitDetails;
